import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { defaultStories } from '../../data/products';
import { 
  Sparkles, 
  Plus, 
  Trash2, 
  Save, 
  RotateCcw, 
  Edit3, 
  X, 
  Upload, 
  Eye,
  Layers
} from 'lucide-react';

export default function AdminStoriesManager() {
  const { storyHighlightsData, categories, addStory, updateStory, deleteStory, addToast } = useShop();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const initialForm = {
    title: '',
    img: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=300&q=80',
    category: 'bridal',
    badge: 'NEW'
  };

  const [form, setForm] = useState(initialForm);

  const openAddModal = () => {
    setEditingId(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEditModal = (story) => {
    setEditingId(story.id);
    setForm({ ...story });
    setModalOpen(true);
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      addToast('Please enter a story title', 'error');
      return;
    }

    if (editingId) {
      updateStory(editingId, form);
    } else {
      addStory(form);
    }
    setModalOpen(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      addToast('Image size should be under 4MB', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setForm((prev) => ({ ...prev, img: event.target?.result }));
      addToast('Story cover image loaded!', 'info');
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ maxWidth: '1000px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--gold-400)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.3rem' }}>
            <Sparkles size={13} /> Mobile-First Story Highlights CMS
          </div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff' }}>
            Instagram-Style <span className="text-gold-gradient">Story Highlights</span>
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Manage the circular story icons displayed just below the navbar for fast mobile browsing.
          </p>
        </div>

        <button
          onClick={openAddModal}
          className="btn btn-gold btn-sm"
          style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.82rem' }}
        >
          <Plus size={16} />
          <span>Add Story Highlight</span>
        </button>
      </div>

      {/* Live Preview Strip */}
      <div style={{
        background: '#0d0d16',
        border: '1px solid var(--border-gold)',
        borderRadius: 'var(--radius-md)',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem', color: 'var(--gold-400)', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase' }}>
          <Eye size={15} /> Storefront Story Row Preview
        </div>

        <div style={{
          display: 'flex',
          gap: '1.5rem',
          overflowX: 'auto',
          paddingBottom: '0.5rem',
          alignItems: 'center'
        }}>
          {storyHighlightsData.map((st) => (
            <div key={st.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
              <div style={{
                position: 'relative',
                width: '68px',
                height: '68px',
                borderRadius: '50%',
                padding: '2.5px',
                background: 'linear-gradient(135deg, #d4af37 0%, #fef08a 50%, #92400e 100%)',
                boxShadow: '0 0 15px rgba(212, 175, 55, 0.25)'
              }}>
                <img
                  src={st.img}
                  alt={st.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #0d0d16'
                  }}
                />
                {st.badge && (
                  <span style={{
                    position: 'absolute',
                    bottom: '-2px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#92400e',
                    color: '#fef08a',
                    fontSize: '0.58rem',
                    fontWeight: 800,
                    padding: '0.05rem 0.35rem',
                    borderRadius: '999px',
                    border: '1px solid rgba(254, 240, 138, 0.4)',
                    whiteSpace: 'nowrap'
                  }}>
                    {st.badge}
                  </span>
                )}
              </div>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff', textAlign: 'center', maxWidth: '75px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {st.title}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Stories Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.25rem'
      }}>
        {storyHighlightsData.map((story) => (
          <div
            key={story.id}
            style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
              <img
                src={story.img}
                alt={story.title}
                style={{
                  width: '54px',
                  height: '54px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid var(--border-gold)'
                }}
              />
              <div>
                <div style={{ fontWeight: 800, color: '#fff', fontSize: '0.92rem' }}>
                  {story.title}
                </div>
                <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                  Target: <strong style={{ color: 'var(--gold-400)' }}>{story.category}</strong>
                  {story.badge && ` • [${story.badge}]`}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button
                onClick={() => openEditModal(story)}
                className="btn btn-glass btn-sm"
                style={{ padding: '0.35rem 0.6rem' }}
                title="Edit Story"
              >
                <Edit3 size={15} color="var(--gold-400)" />
              </button>
              <button
                onClick={() => deleteStory(story.id)}
                style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '0.35rem' }}
                title="Remove Story"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit Modal */}
      {modalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1.5rem'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '460px',
            backgroundColor: '#13131e',
            border: '1px solid var(--border-gold)',
            borderRadius: 'var(--radius-md)',
            padding: '2rem',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff' }}>
                  {editingId ? 'Edit Story Highlight' : 'New Story Highlight'}
                </h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Appears in circular reels strip on the mobile storefront.
                </p>
              </div>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Story Title *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Bridal 2026"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="input-luxury"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Category Link Filter
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="input-luxury"
                >
                  <option value="all">All Collections</option>
                  {(categories || []).map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Badge Tag (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. NEW, 15% OFF, IGI"
                  value={form.badge}
                  onChange={(e) => setForm({ ...form, badge: e.target.value })}
                  className="input-luxury"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Cover Image
                </label>
                <input
                  type="url"
                  placeholder="https://..."
                  value={form.img}
                  onChange={(e) => setForm({ ...form, img: e.target.value })}
                  className="input-luxury"
                  style={{ marginBottom: '0.4rem' }}
                />
                <label className="btn btn-glass btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <Upload size={14} />
                  <span>Or Upload From Device</span>
                  <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} />
                </label>
              </div>

              <div style={{ display: 'flex', gap: '0.8rem', marginTop: '1rem' }}>
                <button type="button" onClick={() => setModalOpen(false)} className="btn btn-glass" style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-gold" style={{ flex: 1 }}>
                  Save Story
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
